// Copyright ©️ 2026 eVolve MEP, LLC

import type { MachineElement } from '@evolvemep/api-ts';

import { GridMenu } from 'components/Mantine/GridMenu';
import { isNil } from 'helpers/isNotNil';
import { useHasAccess } from 'hooks/user/useHasAccess';

import { useMachineElementActions } from './machineElementActions/useMachineElementActions';

type Props = {
  machineElement: MachineElement;
  onElementUpdated: (updatedElement: MachineElement) => void;
  onEditElementData?: (element: MachineElement) => void;
  onEditBendData?: (element: MachineElement) => void;
  onRemoveElement?: (element: MachineElement) => void;
  onChangeBenderProfile?: (element: MachineElement) => void;
};

export const ElementMenu = ({
  machineElement,
  onElementUpdated,
  onEditElementData,
  onEditBendData,
  onRemoveElement,
  onChangeBenderProfile,
}: Props) => {
  const { markElementDone, markElementUnfinished, mutationRunning, checkIfElementIsUpdating } =
    useMachineElementActions();
  const hasFabricationFullAccess = useHasAccess('fabrication', 'write');

  if (!hasFabricationFullAccess) {
    return null;
  }

  return (
    <GridMenu
      options={[
        {
          value: 'Mark bend done',
          hidden: !machineElement.canMarkBendDone,
        },
        {
          value: 'Mark bend unfinished',
          hidden: isNil(machineElement.bendMarkedDoneOn),
        },
        {
          value: 'Mark cut done',
          hidden: !machineElement.canMarkCutDone,
        },
        {
          value: 'Mark cut unfinished',
          hidden: isNil(machineElement.cutMarkedDoneOn),
        },
        {
          value: 'Update bend values',
          hidden: !machineElement.hasBendData || machineElement.source !== 'BendCreator' || isNil(onEditBendData),
        },
        {
          value: 'Update bend properties',
          hidden: !machineElement.hasBendData || machineElement.source !== 'BendCreator' || isNil(onEditElementData),
        },
        {
          value: 'Change bender profile',
          hidden: !machineElement.hasBendData || isNil(onChangeBenderProfile),
        },
        {
          value: 'Remove from work order',
          color: 'red',
          hidden: isNil(machineElement.workOrderId) || isNil(onRemoveElement),
        },
      ]}
      loading={checkIfElementIsUpdating(machineElement.elementId)}
      disabled={mutationRunning}
      onMenuItemSelected={async (menuItemSelected) => {
        switch (menuItemSelected) {
          case 'Mark bend done': {
            await markElementDone(machineElement, 'Bend', onElementUpdated);
            break;
          }
          case 'Mark bend unfinished': {
            await markElementUnfinished(machineElement, 'Bend', onElementUpdated);
            break;
          }
          case 'Mark cut done': {
            await markElementDone(machineElement, 'Cut', onElementUpdated);
            break;
          }
          case 'Mark cut unfinished': {
            await markElementUnfinished(machineElement, 'Cut', onElementUpdated);
            break;
          }
          case 'Update bend values':
            onEditBendData?.(machineElement);
            break;
          case 'Update bend properties':
            onEditElementData?.(machineElement);
            break;
          case 'Change bender profile':
            onChangeBenderProfile?.(machineElement);
            break;
          case 'Remove from work order':
            onRemoveElement?.(machineElement);
            break;
        }
      }}
    />
  );
};
